import {ColumnElement} from "./column-element.js";
import {TiberForm} from "../tiber-form.js";

/**
 * Class representing a form element within a UI component.
 * Extends from `ColumnElement` to inherit layout functionality.
 *
 * This class provides mechanisms to define, handle, and validate
 * various form-related components as part of a form structure.
 * It manages attributes like visibility, field data, and validation
 * in context to its parent collection and elements.
 */
export class FormElement extends ColumnElement{
    formData;
    parentElement;
    collection;
    componentTag;
    domElement;
    destroyed;
    domComponent;
    requiredSpan;

    /** Configuration Structure **/
    field;
    label;
    required;
    requiredLabel;
    columnSpan;
    cssClass;
    visible;

    updates;

    /**
     * Retrieves the full hierarchical field path by concatenating the parent's field path (if available) with the current field.
     * If the current field is empty, only the parent's field path is returned. If there is no parent, the current field is returned.
     *
     * @return {string|null} The resolved field path as a string, or null if no path is available.
     */
    get fieldPath() {
        let parentPath = null;

        if (this.parent != null) {
             parentPath = this.parent.fieldPath;
        }

        if ((this.field ?? "").length === 0) {
            return parentPath;
        }

        if ((parentPath ?? "").length === 0) {
            return this.field;
        }

        return `${parentPath}.${this.field}`;
    }

    /**
     * Retrieves the value of the 'field' property. If the 'field' property is not defined for the current instance,
     * the method attempts to retrieve the 'field' property from the parent object, if available.
     *
     * @return {*} The value of the 'field' property from the current instance or the parent object's 'field' property.
     */
    get dataField() {
        return this.field ?? this.parent?.field;
    }

    /**
     * Retrieves the value of the specified data field from the form data.
     *
     * @return {*} The value associated with the dataField key in the formData object.
     */
    get dataValue() {
        return this.formData[this.dataField];
    }

    /**
     * Sets the value of the specified data field in the formData object.
     * @param {any} value - The value to be assigned to the data field.
     */
    set dataValue(value) {
        this.formData[this.dataField] = value
    }

    /**
     * Determines the visibility of an element based on certain conditions, including device type,
     * visibility configuration, parent visibility, and specified dependencies.
     *
     * @return {boolean} Returns true if the element is visible, otherwise false.
     */
    get isVisible() {
        if (this.collection.isMobile && !(this.showMobile ?? this.visible?.showMobile ?? true)) {
            return false;
        }

        if (!(this.parent?.isVisible ?? true)) {
            return false;
        }

        if (this.visible == null) {
            return true;
        }

        if (typeof this.visible == "boolean") {
            return this.visible;
        }

        if (this.visible.dependsOn == null) {
            return true;
        }

        const dependOn = this.visible.dependsOn;
        const value = this.visible.value === undefined && this.visible.values == undefined ? "*" :  this.visible.value ?? this.visible.values;
        return this.checkData(dependOn, value);
    }

    /**
     * Retrieves the parent form associated with the current collection, if available.
     *
     * @return {Form|null} The parent form of the collection, or null if no parent form exists.
     */
    get parentForm() {
        return this.collection?.parentForm;
    }

    /**
     * Retrieves the root form associated with the current collection.
     *
     * @return {Object|null} The root form object if it exists, or null if it is not available.
     */
    get rootForm() {
        return this.collection?.rootCollection?.parentForm;
    }

    /**
     * Determines if the current object is valid based on its configuration.
     *
     * The method checks multiple conditions:
     * - If the field is not required or not visible, it is considered valid.
     * - If the field is required and visible, its value in the form data must be non-null and:
     *   - For string values, it must not be empty or whitespace only.
     *   - For numerical values, it must not be NaN.
     *
     * @return {boolean} True if the object satisfies the validity conditions, otherwise false.
     */
    get isValid() {
        const _isValid =  !this.isVisible || this.validateRequired();

        return _isValid;
    }

    /**
     * Gets the width of the component based on its bounding client rectangle.
     * This method retrieves the offset width if available, otherwise it falls back to the width property.
     *
     * @return {number} The width of the component in pixels.
     */
    get componentWidth() {
        const bounds = this.domElement.getBoundingClientRect();
        return bounds.offsetWidth ?? bounds.width;
    }

    /**
     * Determines if the current context is considered mobile.
     * The method checks the `mobileBounds` property to decide
     * whether the environment qualifies as mobile. If `mobileBounds`
     * is not set, it falls back to the `isMobile` property of the parent form.
     *
     * @return {boolean} Returns true if the current context is mobile, otherwise false.
     */
    isMobile() {
        if (this.mobileBounds == null) {
            return this.parentForm?.isMobile ?? false;
        }

        return this.mobileBounds < this.componentWidth ?? false;
    }

    /**
     * CSTOR
     * @param configuration Configuration for the element
     * @param parent Parent element to attach the form element
     */
    constructor(formData, parentElement, collection) {
        super(() => this.isMobile());

        const contructorExtensions = [
            [FormElement, () => {
                this.parent = parentElement;
                this.parentElement = parentElement.domComponent ?? parentElement.domElement;
            }],
            [TiberForm, () => {
                this.parent = parentElement;
                this.parentElement = parentElement.formContainer;
            }]
        ];
        this.formData = formData;

        this.parentElement = parentElement;
        for (let extension of contructorExtensions) {
            if (parentElement instanceof extension[0]) {
                extension[1]();
                break;
            }
        }

        this.collection = collection;
    }

    /**
     * Sets the value for a specific field in the form data.
     * Updates the dataValue property with the provided value
     * if it is different from the current one.
     *
     * @param {*} value - The new value to be assigned.
     * @return {boolean} Returns true if the value was successfully updated, false otherwise.
     */
    setValue(value) {
        if (this.dataValue === value || (this.dataValue == null && value == null)) {
            return false;
        }
        if (value == null && this.dataValue === undefined) {
            return false;
        }
        if (value == null) {
            delete this.formData[this.field];
        } else {
            this.dataValue = value;
        }

        return true;
    }

    updateSuccessor() {
        if (this.updates == null || this.collection?.elements == null) return;

        const successorElement = this.collection.elements[this.updates];
        if (successorElement == null) return;

        const predecessorElement = this.getPredecessor();
        const successorValue = this.getSuccessorValue() ?? predecessorElement?.dataValue ?? this.dataValue;

        successorElement.setValue(successorValue);
    }

    getPredecessor() {
        if (this.dependsOn == null || this.collection?.elements == null) return;
        return this.collection.elements[this.dependsOn];
    }

    getSuccessorValue() {
        return this.dataValue;
    }

    /**
     * Commands to build the component
     * @returns The HtmlElement that was built to be added to the Wrapper
     */
    component(wrapper) {
        if (this.componentTag == null) {
            return null;
        }

        return document.createElement(this.componentTag);
    }

    /**
     * Sets any attributes for the component
     * @param attributes
     */
    attributes(component) {
        if (component.dataset === undefined) {
            return;
        }

        if (this.dataField != null && this.dataField.length > 0) {
            component.dataset.field = this.dataField;
        }
    }

    /**
     * Creates the wrapper for the component to house the controls
     * @returns {HTMLDivElement}
     */
    componentWrapper() {
        const componentWrapper = document.createElement('div');
        componentWrapper.classList.add(['tiber-form-element']);

        if (this.cssClass != null) {
            let classes = [];
            if (Array.isArray(this.cssClass)) {
                classes = [...this.cssClass];
            } else {
                classes = this.cssClass.split(/\s+/).filter(item => item != null && item.trim().length > 0);
            }

            for (let cssClass of classes) {
                try {
                    if (cssClass != null && cssClass.trim().length > 0) componentWrapper.classList.add(cssClass.trim());
                } catch (error) {
                    console.error(error); // if class is bad  - move one
                }
            }
        }

        const elementGridSpan = this.elementGridSpan(this);
        if (elementGridSpan) {
             componentWrapper.style.gridColumn = this.elementGridSpan(this);
        }

        this.parentElement.appendChild(componentWrapper);
        return componentWrapper;
    }

    /**
     * Label for the component
     * @returns {HTMLLabelElement}
     */
    componentLabel() {
        const label = document.createElement('label');
        label.innerText = this.label;

        if (this.required) {
            this.addRequiredSpan(label);
        }
        return label;
    }

    /**
     * Adds a required span element to the given label.
     *
     * @param {HTMLElement} label - The label element to which the required span will be appended.
     * @return {HTMLElement} The created and appended required span element.
     */
    addRequiredSpan(label) {
        this.requiredSpan = document.createElement('span');
        this.requiredSpan.innerHTML = `&nbsp;${this.requiredLabel ?? "*"}&nbsp;`;
        this.requiredSpan.classList.add(['tiber-form-required']);

        label.appendChild(this.requiredSpan);

        return this.requiredSpan;
    }

    /**
     * Builds the default listeners for the components
     * @param component The component to attach the listener to
     */
    defaultListeners(component) {
        component.addEventListener('change', event => {
            if (this.valueChange(event)) {
                this.emitChangeNotification();
            }
        });

        if (
            (this.changeEvent == null || typeof this.changeEvent !== "function") &&
            (!this.visible == null || (typeof this.visible !== "boolean" && this.visible?.dependsOn != null))
        ) {
            return;
        }

        this.subscribeToChanges();
    }

    /**
     * Subscribes to changes in the associated collection and handles updates.
     * The method listens for data updates and triggers visibility adjustments and change event handlers
     * if the current instance is not marked as destroyed and the data element is not the same as the current instance.
     *
     * @return {void} No return value as the method is used for its side effects.
     */
    subscribeToChanges() {
        this.collection.subscribeToChanges({
            next: data => {
                if ((this.destroyed ?? false) === true) { /* just in case the Garbage collector has not fired yet */
                    return;
                }

                if (data.element !== this) {
                    this.setVisibility();
                    this.changeEvent(data);
                }
            }
        })
    }

    /**
     * Emits a change notification for the current element.
     * The notification includes the element reference, a timestamp, and the updated data field and value.
     *
     * @return {void} Does not return a value.
     */
    emitChangeNotification() {
        this.updateSuccessor();
        if (typeof this.collection.emitChangeNotification !== 'function') return;

        this.collection.emitChangeNotification({
            element: this,
            timestamp: Math.floor(Date.now() / 1000),
            field: this.dataField,
            value: this.dataValue
        });
    }

    /**
     * Extra steps assigned for the OnChange
     * @param event
     */
    valueChange(event) {
        return this.setValue(event.target.value);
    }

    /**
     * Verifies if the given field's value meets certain conditions based on the provided values.
     *
     * @param {string} field - The name of the field to check within the collection.
     * @param {string|string[]|boolean} values - The condition to evaluate against the field's value.
     *        Can be a specific value, an array of values, or a boolean condition.
     * @return {boolean} - Returns true if the field's value satisfies the specified conditions, otherwise false.
     */
    checkData(field, values) {
        let fieldValue = this.collection.getDataValue(field);

        if (fieldValue == null && typeof values === "boolean" ) {
            fieldValue = false;
        }

        return (values === "*" && ((fieldValue ?? "") !== "")) ||
            (Array.isArray(values) && values.includes(fieldValue)) ||
            (values === fieldValue);

    }

    /**
     * Sets the label for a given component and appends it to the wrapper.
     *
     * @param {Object} wrapper - The container element where the label will be appended.
     * @param {Object|null} component - The component associated with the label. If null, no action will be taken.
     * @param {boolean} prepend - Determines the label's placement, though it is not utilized in this method.
     * @return {void} This method does not return a value.
     */
    setLabel(wrapper, component, prepend) {
        if (component == null || (this.label ?? "").length === 0) {
            return;
        }

        wrapper.append(this.componentLabel());
    }

    /**
     * Adds a component to the specified wrapper.
     *
     * @param {HTMLElement} wrapper - The wrapper element*/
    addComponent(wrapper, component) {
        wrapper.append(component);
    }

    /**
     * Handles the event of changing data and performs necessary actions based on the provided input.
     *
     * @param {Object} data - The data object containing the details necessary for processing the event.
     *        The structure and required properties of the object should be defined in the implementation.
     */
    changeEvent(data) {}

    /**
     * Sets the visibility of a DOM element based on the `isVisible` property.
     * If `isVisible` is true, the element's display and visibility styles are reset.
     * If `isVisible` is false and `visible.invisiblePreserveSpace` is true, the visibility
     * is set to "hidden", preserving the layout space of the element.
     * Otherwise, the element's display style is set to "none", removing it from the layout.
     *
     * @return {void} Does not return a value.
     */
    setVisibility(overrideFactory) {
        const isVisible = overrideFactory?.() ?? this.isVisible;
        
        if (this.domElement) {
            if (isVisible) {
                this.domElement.style.display = "";
                this.domElement.style.visibility = "";
            } else {
                if (this.visible?.invisiblePreserveSpace === true) {
                    this.domElement.style.visibility = "hidden";
                } else {
                    this.domElement.style.display = "none";
                }
            }
        }
    };

    /**
     * Sets the style for the specified component.
     *
     * @param {Object} component - The component object whose style is to be set.
     */
    setComponentStyle(component) { /* Nothing to see here */ }

    /**
     * Prepares components or data for rendering before the actual rendering
     * process begins. This method is typically called to perform operations
     * such as setting up state, caching values, or making adjustments to
     * components before they are rendered on the screen.
     *
     * This function does not take any arguments and does not return a value.
     * It is intended to be overridden or customized in subclasses or used
     * within specific rendering workflows to ensure proper setup prior
     * to rendering.
     */
    preRender() { /* Nothing to see here */}
    /**
     * Renders the control to the DOM
     * @returns {FormElement}
     */
    render() {
        this.preRender();
        this.domElement = this.componentWrapper();
        this.domComponent = this.component(this.domElement);

        this.setLabel(this.domElement, this.domComponent);
        this.setVisibility();

        if (this.domComponent != null) {
            this.setComponentStyle(this.domComponent);
            this.attributes(this.domComponent);
            this.addComponent(this.domElement, this.domComponent);
            this.defaultListeners(this.domComponent);
        }

        this.postRender();
        return this;
    }

    /**
     * This method is intended to be called after the rendering process is complete.
     * It serves as a placeholder for executing post-rendering logic and can be overridden
     * in subclasses to provide specific functionality.
     * By default, it does not perform any operations.
     */
    postRender() { /** Nothing to do here - can be overridden */ }

    /**
     * Validates whether the required field constraint is satisfied for the data value.
     *
     * The method checks if the data value is empty or invalid based on its type,
     * and performs the necessary actions such as displaying a required message
     * or clearing any existing error labels. If the field is required and the value
     * is not valid or missing, the method sets an error message and adjusts the display
     * of the associated required span element.
     *
     * @return {boolean} Returns true if the required field constraint is satisfied; otherwise, false.
     */
    validateRequired() {
        const isEmpty =
            this.dataValue == null ||
            (typeof this.dataValue === "string" && this.dataValue.trim().length === 0) || // Empty string
            (typeof this.dataValue === "number" && isNaN(this.dataValue)); // NaN for numbers

        return this.showHideRequired(isEmpty);
    }

    showHideRequired(isEmpty) {
        const dataNeeded = this.required && isEmpty;

        if (this.requiredSpan != null) {
            this.requiredSpan.style.display = dataNeeded ? "inline" : "none";
        } else if (dataNeeded) {
            return this.setErrorMessage(this.requiredLabel ?? "Required", true);
        }
        this.clearErrorLabel();

        return !dataNeeded;
    }

    /**
     * Validates the user input and clears any associated error labels.
     *
     * @return {boolean} Returns true if the input validation passes, otherwise false.
     */
    validateInput() {
        this.clearErrorLabel(true);
        return true;
    }

    /**
     * Clears the error label if it exists by hiding it, removing it from the DOM,
     * and resetting the `errorLabel` property to null.
     *
     * @return {void} This method does not return a value.
     */
    clearErrorLabel(force) {
        if (this.errorLabel != null && (this.errorLabel.dataset.requirederror === "true" || force === true)) {
            this.errorLabel.style.display = "none";
            this.errorLabel.remove();
            this.errorLabel = null;
        }
    }

    /**
     * Sets the error message to be displayed on the form.
     * If the error label does not exist, it creates one and appends it to the DOM element.
     *
     * @param {string} message - The error message to display.
     * @return {boolean} Returns false after setting the error message.
     */
    setErrorMessage(message,requirederror) {
        if (this.errorLabel == null) {
            this.errorLabel = document.createElement('div');
            this.domElement.appendChild(this.errorLabel);
        }
        this.errorLabel.dataset.requirederror = (requirederror ?? false).toString().toLowerCase();
        this.errorLabel.classList.add(['tiber-form-error']);
        this.errorLabel.innerText = message;

        return false;
    }

    /**
     * A method that is automatically invoked after the rendering process is complete.
     *
     * This method is typically used to perform any additional DOM manipulations,
     * attach event listeners, or run logic that depends on the rendered HTML structure.
     *
     * It does not take any parameters and does not return a value.
     */
    postRender() { /* Nothing to see here */ }

    /**
     * Destroys the current instance by removing its associated DOM element
     * and cleaning up references to prepare it for garbage collection.
     *
     * @return {void} No return value.
     */
    destroy() {
        this.domElement.remove();
        this.domElement = null;
        this.destroy = true;
    }
}
